import {
  ideaDraft,
  ideaOf,
  ideaPhotoUrl,
  isIdeaRecipeId,
  sameArticle,
  toListEntry,
  type Ingredient,
  type Recipe,
  type RecipeDraft,
} from '@shh/shared';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { firebase } from '../lib/firebase';
import { useFamily } from './family';
import { shrink } from '../lib/image';
import { locale } from '../lib/locale';

/**
 * Rezepte (1.21): read directly (the whole family, kids and the wall too), written through the
 * server (free limit, shape). Loaded only in "Essen" – the rest of the app never pays for it.
 */
export type StoredRecipe = Recipe & { id: string };

export function useRecipes(): StoredRecipe[] | undefined {
  const { householdId } = useFamily();
  const [recipes, setRecipes] = useState<StoredRecipe[]>();
  useEffect(
    () =>
      onSnapshot(
        collection(firebase().db, `households/${householdId}/recipes`),
        (s) =>
          setRecipes(
            s.docs
              .map((d) => withIdeaPhoto({ ...(d.data() as Recipe), id: d.id }))
              .sort((a, b) => a.title.localeCompare(b.title, 'de')),
          ),
        // offline before the first load or a rule change: an empty collection, not a crash
        () => setRecipes((r) => r ?? []),
      ),
    [householdId],
  );
  return recipes;
}

/**
 * A dish copied from the Ideenbuch keeps the Ideenbuch's photo until the family takes its own
 * (1.22) – the photo is the app's, nothing is copied into the family's data.
 */
function withIdeaPhoto(r: StoredRecipe): StoredRecipe {
  if (r.thumb || r.source.kind !== 'idea') return r;
  return { ...r, thumb: ideaPhotoUrl(r.source.note) };
}

/** An Ideenbuch dish in the shape of a stored recipe – the recipe page and cooking view read it. */
export function ideaRecipe(recipeId: string): StoredRecipe | null {
  const idea = ideaOf(recipeId);
  if (!idea) return null;
  return {
    ...ideaDraft(idea, locale),
    id: recipeId,
    createdBy: null,
    createdAt: 0,
    updatedAt: 0,
    lastCooked: null,
    cookCount: 0,
    thumb: ideaPhotoUrl(idea.id),
  };
}

/** A recipe of the family – or an Ideenbuch dish ("idee-…", 1.22), which is part of the app. */
export function useRecipe(id: string | undefined): StoredRecipe | null | undefined {
  const { householdId } = useFamily();
  const [recipe, setRecipe] = useState<StoredRecipe | null>();
  const idea = useMemo(() => (isIdeaRecipeId(id) ? ideaRecipe(id) : undefined), [id]);
  useEffect(() => {
    if (!id || isIdeaRecipeId(id)) return;
    return onSnapshot(
      doc(firebase().db, `households/${householdId}/recipes/${id}`),
      (d) => setRecipe(d.exists() ? withIdeaPhoto({ ...(d.data() as Recipe), id: d.id }) : null),
      () => setRecipe(null),
    );
  }, [householdId, id]);
  return idea !== undefined ? idea : recipe;
}

const call = async <T>(name: string, data: unknown): Promise<T> =>
  // reading with the AI may take up to 75 s on the server (lib/gemini.ts) – wait a little longer
  (await httpsCallable(firebase().functions, name, { timeout: 85_000 })(data)).data as T;

/** Callables send `undefined` as `null` – the server's schema takes null for the optional id. */
export const saveRecipe = (draft: RecipeDraft, id?: string) =>
  call<{ id: string }>('saveRecipe', { id: id ?? null, draft });
export const deleteRecipe = (id: string) => call('deleteRecipe', { id });
export const addStarterRecipes = () => call<{ added: number }>('addStarterRecipes', {});
export const cookedRecipe = (id: string) => call('cookedRecipe', { id });

export interface ReadRecipe {
  draft: RecipeDraft;
  /** Lines the AI was not sure about – shown as "Bitte prüfen". */
  unsure: string[];
}
// the recipe comes back in the language the person sees (Mike, 16.10.: German photo → English)
export const recipeFromLink = (url: string, ai = false) =>
  call<ReadRecipe>('recipeFromLink', { url, ai, locale });
export const recipeFromImage = (image: { mimeType: string; data: string }, note: string) =>
  call<ReadRecipe>('recipeFromImage', note ? { image, text: note, locale } : { image, locale });

/** Did the server say "this is Plus" or "the limit is reached"? Then the app shows its hint. */
export const isPlusError = (e: unknown) => !!(e as { details?: { plus?: boolean } })?.details?.plus;
/** "Auf dieser Seite kein Rezept" – the link import offers "Mit KI aufräumen". */
export const canTryAi = (e: unknown) => !!(e as { details?: { canAi?: boolean } })?.details?.canAi;
/** The AI helper is switched off – the app shows the way to the settings. */
export const isAiOff = (e: unknown) => (e as { details?: { ai?: string } })?.details?.ai === 'off';

/**
 * The chosen ingredients onto the family's own list – the same "never twice" as typing them in.
 * (With Bring! connected the shopping hook adds them there instead.)
 */
export function ingredientsToList(
  add: (e: { name: string; spec: string }) => Promise<unknown> | void,
  chosen: Ingredient[],
  from: number,
  to: number,
) {
  for (const i of chosen) {
    const { name, spec } = toListEntry(i, from, to);
    void add({ name, spec });
  }
}

/** Is an article already open on the list? ("steht schon drauf" – unticked by default.) */
export const onList = (open: { name: string }[], name: string) =>
  open.some((o) => sameArticle(o.name, name));

/** The family's own photo: full size in its own document, read only on the recipe page. */
export function useRecipePhoto(id: string | undefined, has: boolean): string | undefined {
  const { householdId } = useFamily();
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!id || !has) return;
    return onSnapshot(
      doc(firebase().db, `households/${householdId}/recipes/${id}/photo/main`),
      (d) => setUrl(d.exists() ? `data:image/jpeg;base64,${d.get('data') as string}` : undefined),
      () => setUrl(undefined),
    );
  }, [householdId, id, has]);
  return has ? url : undefined;
}

/** A photo of the dish, scaled on the device: ~1200 px for the page, ~320 px for the tiles. */
export async function setRecipePhoto(id: string, file: File | null) {
  if (!file) return call('setRecipePhoto', { id, photo: null, thumb: null });
  const [photo, thumb] = await Promise.all([shrink(file, 1200, 0.78), shrink(file, 320, 0.7)]);
  return call('setRecipePhoto', { id, photo: photo.data, thumb: thumb.data });
}
